import { VehicleLocation } from '@/models/registry';
import type { VehicleLocation as VehicleLocationType } from '@/models/types';
import { makeAutoObservable } from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import type { BaseStore } from './base';

export class VehicleLocationStore
  implements BaseStore<VehicleLocation, VehicleLocationType>
{
  store: Map<string, VehicleLocation> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  private setVehicleLocationField = <K extends keyof VehicleLocationType>(
    vehicleLocation: VehicleLocation,
    key: K,
    value: VehicleLocationType[K],
  ) => {
    (vehicleLocation as Pick<VehicleLocation, K>)[key] =
      value as VehicleLocation[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<VehicleLocationType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.vehicle_locations.delete(delta.record_id);
      }
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.vehicle_locations.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.vehicle_locations.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.vehicle_locations.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<VehicleLocationType>;
  }) {
    const existingVehicleLocation = this.getById(delta.record_id);
    if (!existingVehicleLocation) return;

    let changed = false;

    const entries = Object.entries(delta.data) as [
      keyof VehicleLocationType,
      VehicleLocationType[keyof VehicleLocationType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingVehicleLocation[key] === newValue) {
        continue;
      }

      this.setVehicleLocationField(existingVehicleLocation, key, newValue);
      changed = true;
    }

    if (!changed) return;
    this.save(existingVehicleLocation);
  }

  getById(id: string): VehicleLocation | undefined {
    return this.store.get(id);
  }

  add(input: VehicleLocationType) {
    const vehicleLocation = new VehicleLocation(input);
    this.store.set(input.id, vehicleLocation);
  }

  // Computed getters - automatically update when store changes
  get all() {
    return Array.from(this.store.values());
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<VehicleLocationType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<VehicleLocationType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.vehicle_locations.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.remove(delta.record_id);
    }

    this.upsert(delta.data);
  }

  upsert(vehicleLocation: VehicleLocationType) {
    const existingVehicleLocation = this.getById(vehicleLocation.id);
    if (existingVehicleLocation) {
      this.replace({ id: vehicleLocation.id, vehicleLocation });
    } else {
      this.add(vehicleLocation);
    }
  }

  replace({
    id,
    vehicleLocation,
  }: {
    id: string;
    vehicleLocation: VehicleLocationType;
  }) {
    const existingVehicleLocation = this.getById(id);
    if (!existingVehicleLocation) return;

    existingVehicleLocation.name = vehicleLocation.name;

    this.save(existingVehicleLocation);
  }

  save(vehicleLocation: VehicleLocation) {
    this.store.set(vehicleLocation.id, vehicleLocation);
  }

  remove(id: string) {
    this.store.delete(id);

    // remove from dexie
    db.vehicle_locations.delete(id);
  }

  async hydrateLocal(): Promise<void> {
    await hydrateFromWorker({
      tableName: 'vehicle_locations',
      entity: 'vehicle_locations',
      modelStore: vehicleLocationStore,
    });
  }
}

export const vehicleLocationStore = new VehicleLocationStore();
